import { FC, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  DataTable,
  InlineNotification,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
} from '@carbon/react';
import { CheckmarkOutline, Close, Edit } from '@carbon/react/icons';
import { formatResource } from 'utils/misc';
import { StatusTag, type PartyStatus } from 'components/status-tag';
import type { DoSectionStatus } from './types-overview';
import {
  confirmOverviewAtom,
  createOverviewResourceAtom,
  overviewUrl,
  revertOverviewAtom,
} from './atoms/overview';
import { OverviewEditPanel } from './overview-edit-panel';
import { OVERVIEW_FIELDS, fieldDisplayValue } from './overview-utils';
import shell from 'pages/afe-catalog/structuring/structuring-module.module.scss';
import styles from 'pages/afe-catalog/structuring/identification.module.scss';

// DO Overview section — the DivisionOrder analog of the AFE Identification
// section. Mirrors `pages/afe-catalog/structuring/identification-section.tsx`
// exactly: a read-only Field/Value Carbon DataTable driven by the shared
// OVERVIEW_FIELDS map, an Edit toolbar action opening the SidePanel, and a
// HATEOAS Mark-as-Correct / Remove-Correct-Status confirmation action.
//
// One difference from AFE Identification: DO Overview has NO extension_data —
// all 12 fields are column-backed on the DivisionOrder, so display + edit read
// straight off `detail.fields` (no second jsonb block). The section status
// vocabulary is the three-state 'not_started' | 'in_progress' | 'complete',
// which maps 1:1 onto PartyStatus.

interface Props {
  documentId: number | string;
}

// DO Overview status is already the three-state vocabulary shared with
// PartyStatus, so the mapping is an identity for those values; 'removed' is not
// a DO Overview status but is handled defensively.
const toPartyStatus = (status: DoSectionStatus): PartyStatus => {
  if (status === 'complete') return 'complete';
  if (status === 'not_started') return 'not_started';
  return 'in_progress';
};

const HEADERS = [
  { key: 'field', header: 'Field' },
  { key: 'value', header: 'Value' },
];

export const OverviewSection: FC<Props> = ({ documentId }) => {
  const detailHref = overviewUrl(documentId);
  const detailAtom = useMemo(
    () => createOverviewResourceAtom(detailHref),
    [detailHref]
  );
  const [detailResource, reloadDetail] = useAtom(detailAtom);
  const { loading, error, data: detail } = formatResource(detailResource);

  const confirmOverview = useSetAtom(confirmOverviewAtom);
  const revertOverview = useSetAtom(revertOverviewAtom);

  const [editOpen, setEditOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = detail?.status ?? 'not_started';
  const isReviewed = status === 'complete';

  // One Field/Value row per mapped field, driven by the shared OVERVIEW_FIELDS
  // map so the label set never drifts from the edit panel. Every DO overview
  // field is column-backed (no extension_data), so display reads straight off
  // `detail.fields`. Missing values render as an em dash.
  const rows = useMemo(() => {
    if (!detail) return [];
    return OVERVIEW_FIELDS.map((def) => ({
      id: def.key,
      field: def.label,
      value: fieldDisplayValue(def, detail.fields),
    }));
  }, [detail]);

  const confirmHref = detail?.resources?.confirmation?.href;

  const handleMarkCorrect = () => {
    if (!confirmHref) return;
    setActionError(null);
    confirmOverview({
      url: confirmHref,
      method: 'POST',
      onSuccess: () => reloadDetail(),
      onError: (m) => setActionError(m ?? 'Failed to mark as correct.'),
    });
  };

  const handleRemoveCorrect = () => {
    if (!confirmHref) return;
    setActionError(null);
    revertOverview({
      url: confirmHref,
      method: 'DELETE',
      onSuccess: () => reloadDetail(),
      onError: (m) => setActionError(m ?? 'Failed to remove correct status.'),
    });
  };

  return (
    <div className={shell.overviewBody}>
      <div className={shell.overviewSectionTitleRow}>
        <h4 className={shell.overviewSectionTitle}>Division Order Overview</h4>
        <StatusTag
          status={toPartyStatus(status)}
          progress={isReviewed ? '1/1' : '0/1'}
        />
      </div>

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
        />
      )}
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Unable to load overview"
          subtitle={error}
        />
      )}

      {loading && !detail ? (
        <Loading withOverlay={false} small description="Loading overview…" />
      ) : (
        detail && (
          <DataTable rows={rows} headers={HEADERS}>
            {({
              rows: dtRows,
              headers,
              getHeaderProps,
              getTableProps,
            }: any) => (
              <TableContainer>
                <TableToolbar size="sm">
                  <TableToolbarContent>
                    {/* The DO v2 mock Overview toolbar has no search box — just the
                        Edit + Mark/Remove actions, right-aligned. */}
                    {/* Edit stays visible-but-disabled once reviewed; the primary
                        button flips between Mark as Correct / Remove Correct
                        Status rather than swapping the whole toolbar (mirrors the
                        AFE Identification section). */}
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Edit}
                      disabled={isReviewed}
                      onClick={() => setEditOpen(true)}
                    >
                      Edit
                    </Button>
                    {isReviewed ? (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={Close}
                        onClick={handleRemoveCorrect}
                      >
                        Remove Correct Status
                      </Button>
                    ) : (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={CheckmarkOutline}
                        disabled={!confirmHref}
                        onClick={handleMarkCorrect}
                      >
                        Mark as Correct
                      </Button>
                    )}
                  </TableToolbarContent>
                </TableToolbar>
                <Table
                  {...getTableProps()}
                  size="sm"
                  className={styles.identTable}
                >
                  <TableHead>
                    <TableRow>
                      {headers.map((h: any) => (
                        <TableHeader
                          {...getHeaderProps({ header: h })}
                          key={h.key}
                        >
                          {h.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dtRows.map((row: any) => (
                      <TableRow key={row.id}>
                        {row.cells.map((cell: any) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        )
      )}

      {detail && (
        <OverviewEditPanel
          open={editOpen}
          documentId={documentId}
          detail={detail}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            reloadDetail();
          }}
        />
      )}
    </div>
  );
};
